// Painel "Recibos de interação ao vivo" — consome do gateway read-only
// (GET /receipts?tail=N + SSE /receipts/stream). Formato de cada recibo:
// {ts, interacao: 'voz'|'texto', seu_tempo_s, itens: {servico_stt,
//   encaminhamento, processamento{agente, simc, tokens{in,out,cache_w,cache_r}}},
//   total_simc}. É recibo, não pagamento on-chain — o custo agêntico
// (LLM tokens + CPU + mem + tempo) fica visível linha a linha.
import { relativeTimeCompact } from './format.js';

const TAIL = 50;
const SSE_RETRY_MS = 3000;
const MAX_RENDER = 30;

const fmtSimc = (n) => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '0';
  if (v === 0) return '0';
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(1);
  if (abs >= 1) return v.toFixed(2);
  if (abs >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};
const fmtInt = (n) => Number(n ?? 0).toLocaleString('pt-BR');
const fmtSecs = (s) => {
  const n = Math.max(0, Math.round(Number(s ?? 0)));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const r = n % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
};

function receiptKey(r) {
  // ts + total é suficiente pra deduplicar no fluxo real (JSONL append-only).
  return `${r.ts}·${r.total_simc}`;
}

function tokenLine(tokens) {
  if (!tokens) return '';
  const { in: tIn = 0, out: tOut = 0, cache_w = 0, cache_r = 0 } = tokens;
  const total = tIn + tOut + cache_w + cache_r;
  const cachePct = total ? Math.round((cache_r / total) * 100) : 0;
  const cacheDominates = cachePct >= 50;
  return `
    <div class="receipt__tokens">
      <span><em>in</em> ${fmtInt(tIn)}</span>
      <span><em>out</em> ${fmtInt(tOut)}</span>
      <span><em>cache w</em> ${fmtInt(cache_w)}</span>
      <span class="${cacheDominates ? 'is-hot' : ''}"><em>cache r</em> ${fmtInt(cache_r)}${cacheDominates ? ` · <strong>${cachePct}%</strong>` : ''}</span>
    </div>
  `;
}

function renderItem({ label, agent, simc, nota, tokens, muted }) {
  const cost = simc > 0 ? `${fmtSimc(simc)} SIMC` : 'grátis';
  return `
    <div class="receipt__item ${muted ? 'is-muted' : ''}">
      <div class="receipt__item-head">
        <span class="receipt__item-label">${label}${agent ? ` · <span class="receipt__item-agent">${agent}</span>` : ''}</span>
        <span class="receipt__item-cost">${cost}</span>
      </div>
      ${nota ? `<div class="receipt__item-note">${nota}</div>` : ''}
      ${tokens ? tokenLine(tokens) : ''}
    </div>
  `;
}

function renderReceipt(r) {
  const stt = r?.itens?.servico_stt ?? {};
  const enc = r?.itens?.encaminhamento ?? {};
  const proc = r?.itens?.processamento ?? {};
  const tsMs = r.ts ? Date.parse(r.ts) : NaN;
  const rel = Number.isFinite(tsMs) ? relativeTimeCompact(tsMs) : '—';
  const badge = r.interacao === 'voz' ? '🎙️ voz' : '💬 texto';

  return `
    <article class="receipt">
      <header class="receipt__head">
        <span class="receipt__kind">${badge}</span>
        <span class="receipt__time" data-ts="${tsMs || ''}">há ${rel}</span>
        <span class="receipt__spacer"></span>
        <span class="receipt__total">
          <span class="receipt__total-label">total</span>
          <strong>${fmtSimc(r.total_simc)}</strong> SIMC
        </span>
      </header>
      <div class="receipt__yours">Seu tempo · <strong>${fmtSecs(r.seu_tempo_s)}</strong> · transparência, não cobrado</div>
      <div class="receipt__items">
        ${renderItem({ label: 'Serviço STT', agent: stt.agente, simc: Number(stt.simc ?? 0), nota: stt.nota })}
        ${renderItem({ label: 'Encaminhamento', simc: Number(enc.simc ?? 0), nota: enc.nota, muted: true })}
        ${renderItem({ label: 'Processamento agêntico', agent: proc.agente, simc: Number(proc.simc ?? 0), nota: proc.nota, tokens: proc.tokens })}
      </div>
    </article>
  `;
}

export function createReceipts(container) {
  const seen = new Set();
  const list = [];

  function push(r) {
    if (!r || typeof r !== 'object') return;
    const key = receiptKey(r);
    if (seen.has(key)) return;
    seen.add(key);
    list.push(r);
  }

  function render() {
    if (!list.length) {
      container.innerHTML = `<div class="feed-empty">Aguardando a primeira interação registrada…</div>`;
      return;
    }
    const items = [...list].sort((a, b) => Date.parse(b.ts || 0) - Date.parse(a.ts || 0)).slice(0, MAX_RENDER);
    container.innerHTML = items.map(renderReceipt).join('');
  }

  function tick() {
    container.querySelectorAll('[data-ts]').forEach((el) => {
      const ts = Number(el.getAttribute('data-ts'));
      if (ts) el.textContent = `há ${relativeTimeCompact(ts)}`;
    });
  }

  return {
    render,
    tick,
    addMany(items) { for (const r of items) push(r); render(); },
    addOne(r) { push(r); render(); },
  };
}

export async function backfillReceipts(apiUrl) {
  const res = await fetch(`${apiUrl}/receipts?tail=${TAIL}`);
  if (!res.ok) throw new Error(`gateway HTTP ${res.status} em /receipts`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export function subscribeReceipts(apiUrl, { onReceipt, onStatus } = {}) {
  let closed = false;
  let es = null;
  let retryTimer = null;

  function open() {
    if (closed) return;
    onStatus?.('reconnecting');
    try {
      es = new EventSource(`${apiUrl}/receipts/stream`);
    } catch (err) {
      console.error('[receipts] falha ao abrir SSE', err);
      schedule();
      return;
    }
    es.onopen = () => onStatus?.('live');
    es.onmessage = (event) => {
      try { onReceipt(JSON.parse(event.data)); }
      catch (err) { console.error('[receipts] payload SSE inválido', err); }
    };
    es.onerror = () => { es?.close(); es = null; if (!closed) schedule(); };
  }

  function schedule() {
    if (retryTimer || closed) return;
    retryTimer = setTimeout(() => { retryTimer = null; open(); }, SSE_RETRY_MS);
  }

  if (typeof EventSource === 'function') open();
  return { close() { closed = true; if (retryTimer) clearTimeout(retryTimer); es?.close(); } };
}
